import React, { useContext, useState } from "react";
import { useHistory } from 'react-router-dom';
import { FormGroup, FormLabel, TextField } from "@mui/material";

import { useSDKLibrary } from "../hooks/useSDKLibrary";
import LoadingSpinner from "./LoadingSpinner";
import { SDKContext } from "../context/sdk";
import { useDesignIdQueryParam } from "../hooks/useDesignIdQueryParam";
import { useTokenQueryParam } from "../hooks/useTokenQueryParam";
import ExternalLink from "./ExternalLink";
import { useVersionQueryParam } from "../hooks/useVersionQueryParam";

/**
 * This handles the form + call to authenticate with sandbox but
 * that same call _also_ loads the SDK, so that logic is here
 * rather than a hook. Could potentially be pulled out into 
 * a function. Also routes from Login to SDK Demo proper.
 */
export default function Login() {
  /**
   * Form state
   */
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');

  /**
   * SDK Library (used to load SDK) and the 
   * SDK itself which we store in React Context for use globally
   */
  const { library } = useSDKLibrary();
  const { error, sdk, setError, setSDK } = useContext(SDKContext);

  // For navigating on successful auth:
  const history = useHistory();

  // For use later on when navigating, so we keep the same design ID:
  const designId = useDesignIdQueryParam();

  // To skip username/password login and go to prod:
  const token = useTokenQueryParam();

  const version = useVersionQueryParam();
  if (version) localStorage.setItem('ASLR::sdkVersion', version);

  // If we haven't loaded the library, we can't authenticate yet:
  if (!library) {
    return (
      <div>
        <span>Loading SDK Library</span>
        <LoadingSpinner />
      </div>
    );
  }

  // If token is defined, log in with that and show loader:
  if (token && !error && !sdk) {
    if (!loading) {
      setLoading(true);
      library.load({
        token,
      }).then((_sdk) => {
        setLoading(false);
        setSDK(_sdk);
        (window as any).sdk = _sdk;
        history.push({
          pathname: '/sdk',
          search: designId ? `?design_id=${designId}` : '',
        });
      }).catch((e) => {
        setError(e?.message);
        setLoading(false);
        console.error(e);
      });
    }
    
    return (
      <div>
        <span>Authenticating</span>
        <LoadingSpinner />
      </div>
    );
  }

  const getDesignIdFromSearch = () => {
    return new URLSearchParams(history.location.search).get('design_id');
  }

  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePass = (e: React.ChangeEvent<HTMLInputElement>) => setPass(e.target.value);
  const handleDesignID = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Change design_id query param on update, if new value:
    const designId = getDesignIdFromSearch();
    if (e.target.value !== designId) {
      history.push({
        pathname: '/',
        search: e.target.value ? `?design_id=${e.target.value}` : '',
      });
    }
  }

  return (
    <div
      style={{
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        border: '2px grey solid',
        borderRadius: '6px',
        padding: '1em',
        margin: '3em auto 0 auto',
        maxWidth: '80%',
        minWidth: '400px',
      }}
    >
      <FormGroup>
        <FormLabel>
          Log in using your{' '}
          <ExternalLink href="https://sandbox.aurorasolar.com">Sandbox</ExternalLink>{' '}
          account{' '}
          <ExternalLink href="https://www.notion.so/aurorasolar/Client-SDK-Demo-App-Auth-1a54b6cd265a47338e313a3d83f5995b">(Authentication Docs)</ExternalLink>
        </FormLabel>
        <TextField
          color="success"
          data-testid="email"
          label="Email"
          required
          defaultValue=""
          onChange={handleEmail}
        />
        <TextField
          sx={{ margin: '12px 0' }}
          color="success"
          data-testid="pw"
          label="Password"
          required
          type="password"
          defaultValue=""
          onChange={handlePass}
        />
        <TextField
          sx={{ margin: '12px 0' }}
          color="success"
          data-testid="designId"
          label="Design ID"
          defaultValue={designId || ''}
          onChange={handleDesignID}
        />
        <button
          data-testid="login-btn"
          disabled={loading}
          onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
            setLoading(true);

            let _sdk = null;
            try {
              _sdk = await library.load({
                username: email,
                password: pass,
              });
              console.log('SDK loaded, logged in as ' + email);
            } catch (err: any) {
              console.error('Login error: ', err);
              setError(err?.message);
              setLoading(false);
              return;
            }

            /**
             * If the library and SDK have loaded properly,
             * this should be good and we can route to the SDK demo:
             */
            if (_sdk && setSDK) {
              console.log('Authenticated, setting SDK on context.');
              setSDK(_sdk);
              (window as any).sdk = _sdk;
              history.push({
                pathname: '/sdk',
                search: designId ? `?design_id=${designId}` : '',
              });
            } else {
              console.error('No SDK or `setSDK` method found.');
              setLoading(false);
              setError('No SDK or `setSDK` method found.');
            }
          }}
        >
          {loading ? 'Pending' : 'Log In'}
          {error ? <p>`Error: ${error}`</p> : ''}
        </button>
      </FormGroup>
    </div>
  );
}
